import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { axiosInstance } from "../../api/api";
import { Routes, TabScreens } from "../../routes";
import { hooks } from "../../hooks";
import { actions } from "../../store/actions";

interface PatrolUser { 
  id: number | string; 
  patrol_id: number | string; 
  patrol_name?: string; 
  user_id: number | string; 
  full_name?: string; 
  created_at?: string; 
  updated_at?: string; 
}

interface Patrol { 
  id: number | string; 
  name: string; 
}

interface User { 
  id: number | string; 
  name: string; 
}

interface Message { 
  type: "success" | "error" | "info"; 
  text: string; 
}

interface FormData { 
  patrol_id: string; 
  user_id: string; 
}

export const usePatrolUsers = () => {
  const [patrolUsers, setPatrolUsers] = useState<PatrolUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<Message | null>(null);

  const setAutoHideMessage = (type: Message["type"], text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const fetchPatrolUsers = async () => {
    try {
      setLoading(true);
      const patrolUsersRes = await axiosInstance.get("/api/v1/patrol/user/index", { 
        params: { per_page: 10, sort_by: "id", sort_type: "desc" } 
      });
      const patrolsRes = await axiosInstance.get("/api/v1/patrol/index");

      const patrols: Patrol[] = patrolsRes.data?.data || [];
      const assignments: PatrolUser[] = patrolUsersRes.data?.data || [];

      const mappedData = assignments.map((p: PatrolUser) => {
        const patrol = patrols.find((pl) => String(pl.id) === String(p.patrol_id));
        return { 
          ...p, 
          patrol_name: patrol ? patrol.name : `Patrol #${p.patrol_id}` 
        };
      });

      setPatrolUsers(mappedData);
    } catch (err: any) {
      console.error(err);
      setAutoHideMessage("error", err.response?.data?.message || "Gagal mengambil data patrol user");
    } finally { 
      setLoading(false); 
    }
  };

  const deletePatrolUser = async (id: number | string) => {
    try {
      if (!id || id === "NaN") throw new Error("Invalid ID");
      await axiosInstance.delete(`/api/v1/patrol/user/destroy/${id}`);
      setPatrolUsers((prev) => prev.filter((p) => p.id !== id));
      setAutoHideMessage("success", "Assignment berhasil dihapus");
    } catch (err: any) {
      console.error(err);
      setAutoHideMessage("error", err.response?.data?.message || "Gagal menghapus patrol user");
    }
  };

  useEffect(() => { 
    fetchPatrolUsers(); 
  }, []);

  return { 
    patrolUsers, 
    loading, 
    message, 
    fetchPatrolUsers, 
    deletePatrolUser, 
    setPatrolUsers,
    setAutoHideMessage
  };
};

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

const ConfirmationModal: React.FC<{
  visible: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
}> = ({ visible, title, message, onConfirm, onCancel, confirmText = "Ya, Hapus", cancelText = "Batal" }) => {
  if (!visible) return null;

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.confirmationModal}>
        <div style={styles.confirmationHeader}>
          <h3 style={styles.confirmationTitle}>{title}</h3>
        </div>
        <div style={styles.confirmationBody}>
          <p style={styles.confirmationMessage}>{message}</p>
        </div>
        <div style={styles.confirmationActions}>
          <button 
            onClick={onCancel}
            style={styles.cancelButton}
          >
            {cancelText}
          </button>
          <button 
            onClick={onConfirm}
            style={styles.confirmDeleteButton}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const MessageAlert: React.FC<{ message: Message | null }> = ({ message }) => {
  if (!message) return null;
  
  const alertStyles = {
    success: { 
      backgroundColor: "#d1fae5", 
      color: "#065f46", 
      border: "1px solid #a7f3d0",
      icon: "✅"
    },
    error: { 
      backgroundColor: "#fee2e2", 
      color: "#991b1b", 
      border: "1px solid #fecaca",
      icon: "❌"
    },
    info: { 
      backgroundColor: "#dbeafe", 
      color: "#1e40af", 
      border: "1px solid #bfdbfe",
      icon: "ℹ️"
    },
  };

  return (
    <div style={{
      ...alertStyles[message.type],
      padding: "12px 16px",
      borderRadius: "8px",
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      fontWeight: "500"
    }}>
      <span>{alertStyles[message.type].icon}</span>
      {message.text}
    </div>
  );
};

const LoadingSpinner: React.FC = () => (
  <div style={styles.loadingContainer}>
    <div style={styles.spinner}></div>
    <p style={styles.loadingText}>Memuat data patrol user...</p>
  </div>
);

const ActionButtons: React.FC<{ onEdit: () => void; onDelete: () => void }> = ({ onEdit, onDelete }) => (
  <div style={styles.actionContainer}>
    <button 
      style={styles.editButton} 
      onClick={onEdit}
      title="Edit"
    >
      <span style={styles.buttonText}>Ubah</span>
    </button>
    <button 
      style={styles.deleteButton} 
      onClick={onDelete}
      title="Hapus"
    >
      <span style={styles.buttonText}>Hapus</span>
    </button>
  </div>
);

const PatrolUserCard: React.FC<{ 
  item: PatrolUser; 
  onEdit: (item: PatrolUser) => void; 
  onDelete: (item: PatrolUser) => void; 
}> = ({ item, onEdit, onDelete }) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div style={styles.cardTitle}>
          <span style={styles.patrolName}>{item.patrol_name}</span>
        </div>
        <div style={styles.cardBadge}>
          <span style={styles.badgeText}>ID: {String(item.id).slice(0, 8)}...</span>
          <button 
            style={styles.copyButton} 
            onClick={() => copyToClipboard(String(item.id))}
            title="Salin ID"
          >
            📋
          </button>
        </div>
      </div>
      
      <div style={styles.cardContent}>
        <div style={styles.cardRow}>
          <span style={styles.cardLabel}>User ID:</span>
          <code style={styles.userId}>{item.user_id}</code>
        </div>
        <div style={styles.cardRow}>
          <span style={styles.cardLabel}>Diupdate:</span>
          <span style={styles.cardValue}>{formatDate(item.updated_at)}</span>
        </div>
      </div>
      
      <div style={styles.cardActions}>
        <ActionButtons 
          onEdit={() => onEdit(item)} 
          onDelete={() => onDelete(item)} 
        />
      </div>
    </div>
  );
};

const PatrolUserTable: React.FC<{ 
  data: PatrolUser[]; 
  onEdit: (item: PatrolUser) => void; 
  onDelete: (item: PatrolUser) => void; 
}> = ({ data, onEdit, onDelete }) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const renderCopyCell = (value: string | number) => (
    <div style={styles.copyCell}>
      <span style={styles.truncatedText}>{String(value).slice(0, 8)}...</span>
      <button 
        style={styles.copyButton} 
        onClick={() => copyToClipboard(String(value))}
        title="Salin ID"
      >
        📋
      </button>
    </div>
  );

  return (
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, width: "120px" }}>ID</th>
            <th style={{ ...styles.th, width: "200px" }}>Nama Patrol</th>
            <th style={styles.th}>User ID</th>
            <th style={styles.th}>Diupdate</th>
            <th style={{ ...styles.th, width: "100px" }}>Aksi</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <tr key={String(item.id)} style={styles.tr}>
              <td style={styles.td}>{renderCopyCell(item.id)}</td>
              <td style={styles.td}>
                <div style={styles.patrolName}>
                  {item.patrol_name}
                </div>
              </td>
              <td style={styles.td}>
                <code style={styles.userId}>{item.user_id}</code>
              </td>
              <td style={styles.td}>{formatDate(item.updated_at)}</td>
              <td style={styles.td}>
                <ActionButtons 
                  onEdit={() => onEdit(item)} 
                  onDelete={() => onDelete(item)} 
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PatrolUserList: React.FC<{ 
  data: PatrolUser[]; 
  onEdit: (item: PatrolUser) => void; 
  onDelete: (item: PatrolUser) => void; 
}> = ({ data, onEdit, onDelete }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (isMobile) {
    return (
      <div style={styles.cardsContainer}>
        {data.map((item) => (
          <PatrolUserCard 
            key={String(item.id)} 
            item={item} 
            onEdit={onEdit} 
            onDelete={onDelete} 
          />
        ))}
      </div>
    );
  }

  return (
    <PatrolUserTable 
      data={data} 
      onEdit={onEdit} 
      onDelete={onDelete} 
    />
  );
};

const FormSelect: React.FC<{ 
  label: string; 
  value: string; 
  options: Array<{ id: string | number; name: string }>; 
  onChange: (value: string) => void; 
}> = ({ label, value, options, onChange }) => (
  <div style={styles.formGroup}>
    <label style={styles.formLabel}>{label}</label>
    <select 
      style={styles.formSelect}
      value={value} 
      onChange={e => onChange(e.target.value)}
    >
      <option value="">-- Pilih {label} --</option>
      {options.map((opt) => (
        <option key={opt.id} value={String(opt.id)}>
          {opt.name}
        </option>
      ))}
    </select>
  </div>
);

const EditPatrolUserModal: React.FC<{ 
  visible: boolean; 
  onClose: () => void; 
  item: PatrolUser | null; 
  onSaveSuccess: (updated: PatrolUser) => void; 
  setAutoHideMessage: (type: Message["type"], text: string) => void;
}> = ({ visible, onClose, item, onSaveSuccess, setAutoHideMessage }) => {
  const [patrols, setPatrols] = useState<Patrol[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState<FormData>({ patrol_id: "", user_id: "" });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  useEffect(() => {
    if (item) {
      setFormData({ 
        patrol_id: String(item.patrol_id), 
        user_id: String(item.user_id) 
      });
      fetchOptions();
    }
  }, [item]);

  const showMessage = (type: Message["type"], text: string) => { 
    setMessage({ type, text }); 
    setTimeout(() => setMessage(null), 3000); 
  };

  const fetchOptions = async () => {
    try {
      const patrolRes = await axiosInstance.get("/api/v1/patrol/index"); 
      setPatrols(patrolRes.data?.data || []);
      
      const userRes = await axiosInstance.get("/api/ap/v1/employee", { 
        params: { page: 1, per_page: 50, role: "security" } 
      });
      setUsers((userRes.data?.data || []).map((u: any) => ({ 
        id: u.user_id, 
        name: `${u.employee_personal?.full_name || 'Unknown'} (${u.user_id})` 
      })));
    } catch (err) { 
      showMessage("error", "Gagal memuat pilihan patrol/user"); 
    }
  };

  const handleSubmit = async () => {
    if (!item?.id) return showMessage("error", "ID item tidak valid");
    if (!formData.patrol_id || !formData.user_id) return showMessage("error", "Harap pilih patrol dan user");

    try {
      setSubmitting(true);

      const payload = { 
        patrol_id: formData.patrol_id, 
        user_id: formData.user_id 
      };

      const response = await axiosInstance.post(
        `/api/v1/patrol/user/update/${item.id}`, 
        payload
      );

      setAutoHideMessage("success", "Data patrol user berhasil diperbarui!");

      onSaveSuccess({
        ...item,
        patrol_id: formData.patrol_id,
        user_id: formData.user_id,
        patrol_name: patrols.find(p => String(p.id) === formData.patrol_id)?.name || item.patrol_name,
      });

      onClose();
    } catch (err: any) {
      console.error("Update error:", err.response?.data || err.message);
      showMessage("error", err.response?.data?.message || "Gagal memperbarui data");
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible || !item) return null;

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Edit Assignment Patrol</h3>
          <button 
            onClick={onClose}
            style={styles.closeButton}
          >
            ✕
          </button>
        </div>
        
        <div style={styles.modalBody}>
          {message && <MessageAlert message={message} />}
          
          <FormSelect 
            label="Patrol" 
            value={formData.patrol_id} 
            options={patrols} 
            onChange={(v) => setFormData(prev => ({ ...prev, patrol_id: v }))} 
          />
          
          <FormSelect 
            label="User" 
            value={formData.user_id} 
            options={users} 
            onChange={(v) => setFormData(prev => ({ ...prev, user_id: v }))} 
          />
        </div>

        <div style={styles.modalActions}>
          <button 
            style={styles.secondaryButton} 
            onClick={onClose} 
            disabled={submitting}
          >
            Batal
          </button>
          <button 
            style={styles.primaryButton} 
            onClick={handleSubmit} 
            disabled={submitting}
          >
            {submitting ? (
              <>
                <div style={styles.buttonSpinner}></div>
                Menyimpan...
              </>
            ) : (
              "Simpan Perubahan"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const PatrolUser: React.FC = () => {
  const navigate = useNavigate(); 
  const dispatch = useDispatch();
  const { patrolUsers, loading, message, deletePatrolUser, setPatrolUsers, setAutoHideMessage } = usePatrolUsers();
  const [editModalVisible, setEditModalVisible] = useState(false); 
  const [editItem, setEditItem] = useState<PatrolUser | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<PatrolUser | null>(null);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleEdit = (item: PatrolUser) => { 
    setEditItem(item); 
    setEditModalVisible(true); 
  };

  const handleDeleteClick = (item: PatrolUser) => { 
    setItemToDelete(item); 
    setDeleteModalVisible(true); 
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete) {
      deletePatrolUser(itemToDelete.id);
      setDeleteModalVisible(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setItemToDelete(null);
  };

  const handleModalSave = (updated: PatrolUser) => {
    setPatrolUsers((prev) => 
      prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
    );
  };

  const handleAdd = () => navigate(Routes.PatrolUserForm.replace(":id?", ""));
  
  const handleBack = () => { 
    dispatch(actions.setScreen(TabScreens.Admin)); 
    navigate("/tab-navigator", { replace: true }); 
  };

  const mobileBottomPadding = isMobile ? "50px" : "16px";

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{
      ...styles.container,
      paddingBottom: `calc(16px + ${mobileBottomPadding})`,
    }}>

      <div style={styles.backContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          <span style={styles.backIcon}>←</span>
        </button>
      </div>

      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerContent}>
            <h1 style={styles.title}>Daftar Patrol User</h1>
            <button style={styles.addButton} onClick={handleAdd}>
              <span style={styles.buttonIcon}>+</span>
              Tambah Patrol User
            </button>
          </div>
        </div>
        <p style={styles.subtitle}>
          Kelola assignment user untuk patrol
        </p>
      </div>

      <MessageAlert message={message} />

      <div style={styles.contentWrapper}>
        {patrolUsers.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>👥</div>
            <h3 style={styles.emptyTitle}>Belum Ada Assignment</h3>
            <p style={styles.emptyText}>
              Mulai dengan menambahkan assignment patrol user pertama Anda
            </p>
            <button style={styles.emptyAddButton} onClick={handleAdd}>
              + Tambah Assignment Pertama
            </button>
          </div>
        ) : (
          <PatrolUserList 
            data={patrolUsers} 
            onEdit={handleEdit} 
            onDelete={handleDeleteClick} 
          />
        )}
      </div>

      <EditPatrolUserModal 
        visible={editModalVisible} 
        onClose={() => setEditModalVisible(false)} 
        item={editItem} 
        onSaveSuccess={handleModalSave} 
        setAutoHideMessage={setAutoHideMessage}
      />

      <ConfirmationModal
        visible={deleteModalVisible}
        title="Hapus Assignment"
        message={`Apakah Anda yakin ingin menghapus assignment patrol "${itemToDelete?.patrol_name}"? Tindakan ini tidak dapat dibatalkan.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Ya, Hapus"
        cancelText="Batal"
      />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { 
    padding: "16px", 
    maxWidth: "1200px", 
    margin: "0 auto",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    boxSizing: "border-box"
  },
  
  backContainer: { 
    marginBottom: "20px",
    flexShrink: 0
  },
  
  backButton: {
    backgroundColor: "#6b7280",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 16px",

    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  },
  
  backIcon: {
    fontSize: "16px"
  },
  
  header: { 
    marginBottom: "24px",
    flexShrink: 0 
  },
  
  headerTop: {
    marginBottom: "8px"
  },
  
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "20px",
    width: "100%"
  },
  
  title: { 
    fontSize: "24px", 
    fontWeight: 700, 
    color: "#1f2937", 
    margin: 0,
    flex: 1
  },
  
  subtitle: {
    fontSize: "14px",
    color: "#6b7280",
    margin: 0
  },
  
  addButton: { 
    padding: "12px 20px", 
    borderRadius: "8px", 
    border: "none", 
    backgroundColor: "#059669", 
    color: "white", 

    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 4px rgba(5, 150, 105, 0.3)",
    whiteSpace: "nowrap",
    flexShrink: 0
  },
  
  buttonIcon: { 
    fontSize: "16px" 
  },
  
  contentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  
  cardsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "8px 0",
    overflowY: "auto",
    flex: 1,
    paddingBottom: "0px",
  },
  
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flexShrink: 0
  },
  
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "8px"
  },
  
  cardTitle: {
    flex: 1
  },
  
  cardBadge: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "#f3f4f6",
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "12px"
  },
  
  badgeText: {
    fontFamily: "monospace",
    color: "#374151"
  },
  
  cardContent: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  
  cardRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0"
  },
  
  cardLabel: {
    fontSize: "14px",
    color: "#6b7280",
    fontWeight: "500"
  },
  
  cardValue: {
    fontSize: "14px",
    color: "#374151"
  },
  
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: "8px",
    borderTop: "1px solid #f3f4f6"
  },
  
  tableContainer: { 
    overflowX: "auto",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    marginBottom: "20px",
  },
  
  table: { 
    width: "100%", 
    borderCollapse: "collapse",
    minWidth: "600px"
  },
  
  th: { 
    padding: "16px", 
    borderBottom: "1px solid #e5e7eb", 
    textAlign: "left",
    backgroundColor: "#f9fafb",
    fontSize: "14px",
    fontWeight: "600",
    color: "#374151",
    position: "sticky",
    top: 0,
    zIndex: 10
  },
  
  td: { 
    padding: "16px", 
    borderBottom: "1px solid #f3f4f6",
    fontSize: "14px",
    color: "#4b5563"
  },
  
  tr: {
    transition: "background-color 0.2s ease"
  },
  
  copyCell: { 
    display: "flex", 
    alignItems: "center", 
    whiteSpace: "nowrap",
    gap: "8px"
  },
  
  truncatedText: {
    fontFamily: "monospace",
    fontSize: "12px",
    color: "#6b7280"
  },
  
  copyButton: { 
  
    background: "none", 
    border: "none",
    fontSize: "12px",
    padding: "4px",
    borderRadius: "4px",
    transition: "background-color 0.2s ease"
  },
  
  patrolName: {
    fontWeight: "500",
    color: "#1f2937"
  },
  
  userId: {
    backgroundColor: "#f3f4f6",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    fontFamily: "monospace",
    color: "#374151"
  },
  
  actionContainer: { 
    display: "flex", 
    gap: "8px" 
  },
  
  editButton: { 
  
    backgroundColor: "#f3b954",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    transition: "background-color 0.2s ease",
    fontSize: "13px",
    fontWeight: "500",
    minWidth: "60px"
  },
  
  deleteButton: { 
  
    backgroundColor: "#ec4747", 
    color: "white", 
    border: "none", 
    padding: "8px 16px", 
    borderRadius: "6px",
    transition: "background-color 0.2s ease",
    fontSize: "13px",
    fontWeight: "500",
    minWidth: "60px"
  },
  
  buttonText: {
    fontSize: "13px",
    fontWeight: "500"
  },
  
  modalOverlay: { 
    position: "fixed", 
    top: 0, 
    left: 0, 
    right: 0, 
    bottom: 0, 
    backgroundColor: "rgba(0,0,0,0.5)", 
    display: "flex", 
    alignItems: "center", 
    justifyContent: "center", 
    zIndex: 1000,
    padding: "16px",
    paddingBottom: "calc(16px + 40px)",
  },
  
  modalContent: { 
    backgroundColor: "#fff", 
    padding: "0",
    borderRadius: "12px", 
    width: "100%",
    maxWidth: "480px",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
    maxHeight: "90vh",
    overflow: "auto"
  },
  
  modalHeader: {
    padding: "24px 24px 0 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start"
  },
  
  modalTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1f2937",
    margin: 0
  },
  
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "18px",

    padding: "4px",
    borderRadius: "4px",
    color: "#6b7280",
    transition: "color 0.2s ease"
  },
  
  modalBody: {
    padding: "24px"
  },
  
  modalActions: {
    padding: "0 24px 24px 24px",
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end"
  },
  
  formGroup: {
    marginBottom: "20px"
  },
  
  formLabel: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
    marginBottom: "6px"
  },
  
  formSelect: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    backgroundColor: "white",
    transition: "border-color 0.2s ease"
  },
  
  primaryButton: {
    padding: "10px 20px",
    backgroundColor: "#059669",
    color: "white",
    border: "none",
    borderRadius: "8px",
 
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "background-color 0.2s ease"
  },
  
  secondaryButton: {
    padding: "10px 20px",
    backgroundColor: "#6b7280",
    color: "white",
    border: "none",
    borderRadius: "8px",

    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s ease"
  },
  
  buttonSpinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTop: "2px solid white",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  
  loadingContainer: { 
    display: "flex", 
    flexDirection: "column", 
    alignItems: "center", 
    justifyContent: "center", 
    padding: "80px 20px", 
    gap: "16px" 
  },
  
  spinner: { 
    width: "40px", 
    height: "40px", 
    border: "4px solid #f3f3f3", 
    borderTop: "4px solid #059669", 
    borderRadius: "50%", 
    animation: "spin 1s linear infinite" 
  },
  
  loadingText: {
    fontSize: "16px",
    color: "#6b7280",
    margin: 0
  },
  
  emptyState: { 
    textAlign: "center", 
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center"
  },
  
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "16px"
  },
  
  emptyTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#374151",
    margin: "0 0 8px 0"
  },
  
  emptyText: {
    fontSize: "16px",
    color: "#6b7280",
    margin: "0 0 24px 0"
  },

  emptyAddButton: {
    padding: "12px 20px", 
    borderRadius: "8px", 
    border: "none", 
    backgroundColor: "#059669", 
    color: "white", 

    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 4px rgba(5, 150, 105, 0.3)",
    whiteSpace: "nowrap"
  },

  confirmationModal: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "0",
    width: "100%",
    maxWidth: "400px",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)"
  },
  
  confirmationHeader: {
    padding: "24px 24px 0 24px"
  },
  
  confirmationTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1f2937",
    margin: 0
  },
  
  confirmationBody: {
    padding: "16px 24px"
  },
  
  confirmationMessage: {
    fontSize: "14px",
    color: "#4b5563",
    lineHeight: "1.5",
    margin: 0
  },
  
  confirmationActions: {
    padding: "0 24px 24px 24px",
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end"
  },
  
  confirmDeleteButton: {
    padding: "10px 20px",
    backgroundColor: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "8px",

    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s ease"
  },
  
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#6b7280",
    color: "white",
    border: "none",
    borderRadius: "8px",

    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s ease"
  }
};

if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @media (max-width: 767px) {
      .header-content {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
      }
      
      .add-button {
        width: 100%;
        justify-content: center;
      }
    }
    
    @media (min-width: 768px) {
      .header-content {
        flex-direction: row;
        align-items: center;
      }
      
      .add-button {
        width: auto;
      }
      
      .container {
        padding: 20px;
      }
      
      .title {
        font-size: 28px;
      }
    }
  `;
  
  document.head.appendChild(styleSheet);
}